/* eslint-disable @typescript-eslint/no-inferrable-types */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { CircleX, Eye, EyeOff, Info } from "lucide-react";
import ToolTip from "../../../ToolTip/ToolTip";
import { CopyableInput } from "./CopyableInput";
import { InputProps } from "../interfaces";

// Constants
const DATETIME_LOCAL_TYPE = "datetime-local" as const;
const DEFAULT_PLACEHOLDER: string = "Escribe algo";

// Helper function to normalize input values
const normalizeValue = (val: string | number | undefined | null): string => {
  if (val === undefined || val === null) {
    return "";
  }
  return typeof val === "string" ? val : String(val);
};

export const Input: React.FC<InputProps> = (props) => {
  const {
    autoFocus,
    className,
    disabled,
    error,
    handleDelete,
    icon: Icon,
    id,
    isCopyable,
    isRequired,
    label,
    leftAdornment,
    limit = 200,
    name,
    onChange,
    onKeyDown,
    placeholder = DEFAULT_PLACEHOLDER,
    reference,
    rightAdornment,
    style,
    tooltipText,
    type = "text",
    value,
    onBlur: externalOnBlur,
    onFocus: externalOnFocus,
    ...remainingProps
  } = props;
  const [inputType, setInputType] = useState<string>(type);
  // Local state for datetime-local to prevent premature closing of picker
  const [localDateTimeValue, setLocalDateTimeValue] = useState<string>(
    normalizeValue(value)
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const isPickerOpenRef = useRef<boolean>(false);
  const pendingValueRef = useRef<string>(""); // Store value while picker is open without updating state
  const lastBlurValueRef = useRef<string>(""); // Store the last value that was sent to parent on blur
  const isTypingRef = useRef<boolean>(false); // Track if user is typing with keyboard

  // Helper function to get final datetime value from multiple sources
  const getFinalDateTimeValue = useCallback((
    currentTarget: HTMLInputElement | null,
    pendingValue: string,
    localValue: string
  ): string => {
    // Priority: pendingValueRef (user's latest selection) > currentTarget > inputRef > localDateTimeValue
    if (pendingValue) {
      return pendingValue;
    }
    if (currentTarget?.value) {
      return currentTarget.value;
    }
    if (inputRef.current?.value) {
      return inputRef.current.value;
    }
    return localValue || "";
  }, []);

  const handleInputType = (): void =>
    setInputType(inputType === "password" ? "text" : "password");

  const handleChange = (e: React.FormEvent<HTMLInputElement>): void => {
    if (!e.currentTarget) {
      return;
    }
    
    if (inputType === "number") {
      let value: string = e.currentTarget.value;
      
      // Auto-prepend "0" when value starts with "." to allow formats like .8 -> 0.8
      if (value.startsWith(".")) {
        value = "0" + value;
      }
      
      // Allow integers, decimals, and values ending with a dot (while typing)
      const isNumber: RegExp = /^[0-9]*\.?[0-9]*$/;
      if (isNumber.test(value)) {
        const number: number = Number(value);
        if (number < 0) {
          return onChange?.("0");
        }
        return onChange?.(value);
      } else {
        if (e.currentTarget.value === "") {
          return onChange?.("");
        }
        if (e.currentTarget.value.length > limit) {
          return;
        }
        return;
      }
    }
    
    // For datetime-local, handle typing vs picker interaction differently
    if (inputType === DATETIME_LOCAL_TYPE) {
      const newValue: string = e.currentTarget.value || "";
      // Store value in pending ref for blur handler
      pendingValueRef.current = newValue;
      
      // Only update local state if user is typing with keyboard
      // This allows typing to work while preventing picker from closing
      if (isTypingRef.current) {
        setLocalDateTimeValue(newValue);
        isTypingRef.current = false;
      }
      
      // Don't call parent onChange while picker is open - will be called on blur
      if (isPickerOpenRef.current) {
        return;
      }
      // Only call onChange if picker is closed (e.g., programmatic changes)
      onChange?.(newValue);
      return;
    }
    
    if (e.currentTarget.value.length > limit) {
      return;
    }
    onChange?.(e.currentTarget.value || "");
  };

  // Handle input event for datetime-local (fires while user is typing/selecting)
  const handleInput = (e: React.FormEvent<HTMLInputElement>): void => {
    if (inputType === DATETIME_LOCAL_TYPE && e.currentTarget && isPickerOpenRef.current) {
      // Store value in ref to capture it while picker is open
      // Don't update state here to prevent picker from closing
      pendingValueRef.current = e.currentTarget.value || "";
    }
  };

  // Handle keyDown for datetime-local to detect keyboard typing
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (inputType === DATETIME_LOCAL_TYPE) {
      // Mark that user is typing with keyboard (numbers, arrows, backspace, delete, tab)
      const isTypingKey: boolean = 
        /^[0-9]$/.test(e.key) || 
        e.key === "Backspace" || 
        e.key === "Delete" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "Tab";
      
      if (isTypingKey) {
        isTypingRef.current = true;
      }
    }
    // Call original onKeyDown if provided
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  // Handle focus to track when picker opens
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    if (inputType === DATETIME_LOCAL_TYPE) {
      isPickerOpenRef.current = true;
      // Initialize pending value with current value
      pendingValueRef.current = e.currentTarget?.value || localDateTimeValue || "";
    }
    // Call original onFocus if provided
    if (externalOnFocus) {
      externalOnFocus(e);
    }
  };

  // Handle blur for datetime-local to update parent state only when user finishes selection
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    if (inputType === DATETIME_LOCAL_TYPE) {
      // Mark picker as closed first
      isPickerOpenRef.current = false;
      
      // Capture the value from multiple sources using helper function
      // IMPORTANT: If pendingValueRef has a value (user made changes while picker was open),
      // it takes priority because the controlled input value may not have updated yet
      const finalValue: string = getFinalDateTimeValue(
        e.currentTarget,
        pendingValueRef.current,
        localDateTimeValue
      );
      
      // Update local state
      setLocalDateTimeValue(finalValue);
      
      // Store the value we're sending to parent
      lastBlurValueRef.current = finalValue;
      
      // Always call onChange to ensure parent is updated, even if value appears unchanged
      // This is important when editing existing values
      if (onChange) {
        onChange(finalValue);
      }
      
      // Clear pending value
      pendingValueRef.current = "";
    }
    // Call original onBlur if provided
    if (externalOnBlur) {
      externalOnBlur(e);
    }
  };

  useEffect(() => {
    setInputType(type);
  }, [type]);

  // Sync local datetime value with prop value when it changes externally
  // Only sync when picker is closed to avoid interfering with user editing
  useEffect(() => {
    if (inputType !== DATETIME_LOCAL_TYPE || isPickerOpenRef.current) {
      return;
    }
    
    const stringValue: string = normalizeValue(value);
    
    // Only sync if the value from parent is different from what we last sent
    // This prevents overwriting user edits if parent hasn't updated yet
    setLocalDateTimeValue((prevLocalValue) => {
      if (stringValue !== lastBlurValueRef.current && stringValue !== prevLocalValue) {
        // Update last blur value to track what we're syncing
        lastBlurValueRef.current = stringValue;
        return stringValue;
      }
      return prevLocalValue;
    });
  }, [value, inputType]); // Using functional update to avoid needing localDateTimeValue in dependencies


  return isCopyable ? (
    <CopyableInput
      isRequired={isRequired}
      label={label}
      tooltipText={tooltipText}
      value={value}
    />
  ) : (
    <div className="flex w-full flex-col gap-2">
      {label && (
        <div className="flex items-center gap-2">
          <label className="text-sm font-normal text-gray-500">
            {label}{" "}
            {isRequired && (
              <span className="text-xs font-normal text-gray-500">*</span>
            )}
          </label>
          {tooltipText && (
            <ToolTip text={tooltipText}>
              <Info className="h-3 w-3 shrink-0 text-gray-500" />
            </ToolTip>
          )}
        </div>
      )}
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3">
            <Icon className="h-4 w-4 shrink-0 stroke-gray-400" />
          </div>
        )}
        {leftAdornment && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3">
            {leftAdornment}
          </div>
        )}
        <input
          id={id}
          ref={reference || inputRef}
          name={name}
          type={inputType}
          autoFocus={autoFocus}
          className={`h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none disabled:bg-gray-300/20 disabled:text-gray-500 disabled:opacity-100 ${
            Icon || leftAdornment ? "pl-9" : ""
          }
        ${handleDelete || rightAdornment || type === "password" ? "pr-9" : ""}
        ${className ? className : ""}
        ${
          inputType === "number"
            ? "appearance-none [&::-moz-inner-spin-button]:appearance-none [&::-moz-outer-spin-button]:appearance-none [&::-ms-inner-spin-button]:appearance-none [&::-ms-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            : ""
        }
        ${
          inputType === DATETIME_LOCAL_TYPE && !localDateTimeValue
            ? "appearance-none [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-[13px] [&::-webkit-calendar-picker-indicator]:top-[11px] [&::-webkit-calendar-picker-indicator]:flex [&::-webkit-calendar-picker-indicator]:items-center [&::-webkit-calendar-picker-indicator]:justify-center [&::-webkit-datetime-edit-ampm-field]:hidden [&::-webkit-datetime-edit-day-field]:hidden [&::-webkit-datetime-edit-fields-wrapper]:hidden [&::-webkit-datetime-edit-hour-field]:hidden [&::-webkit-datetime-edit-minute-field]:hidden [&::-webkit-datetime-edit-month-field]:hidden   [&::-webkit-datetime-edit-second-field]:hidden [&::-webkit-datetime-edit-year-field]:hidden [&::-webkit-datetime-edit]:hidden"
            : ""
        }
        `}
          placeholder={placeholder}
          style={style}
          value={inputType === DATETIME_LOCAL_TYPE ? localDateTimeValue : value}
          onChange={handleChange}
          onInput={inputType === DATETIME_LOCAL_TYPE ? handleInput : undefined}
          onFocus={inputType === DATETIME_LOCAL_TYPE ? handleFocus : externalOnFocus}
          onBlur={handleBlur}
          disabled={disabled}
          onWheel={(e) => e.currentTarget.blur()}
          onKeyDown={inputType === DATETIME_LOCAL_TYPE ? handleKeyDown : onKeyDown}
          {...remainingProps}
        />
        {rightAdornment && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {rightAdornment}
          </div>
        )}
        {handleDelete && (
          <div
            className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3"
            onClick={handleDelete}
          >
            <CircleX className="h-4 w-4 shrink-0 stroke-gray-400" />
          </div>
        )}
        {type === "password" && (
          <div
            className="absolute inset-y-0 right-0 flex cursor-pointer items-center pr-3"
            onClick={handleInputType}
          >
            {inputType === "password" ? (
              <Eye className="h-4 w-4 shrink-0 stroke-primary" />
            ) : (
              <EyeOff className="h-4 w-4 shrink-0 stroke-primary" />
            )}
          </div>
        )}
        {inputType === DATETIME_LOCAL_TYPE && !localDateTimeValue && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex h-10  items-center pl-3">
            <span className="text-sm text-gray-500">
              Seleccionar fecha y hora
            </span>
          </div>
        )}
      </div>
      {error && <label className="text-xs text-red-500">{error}</label>}
    </div>
  );
};
